import { useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';

export default function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');
    try {
      if (mode === 'signin') {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        onSuccess();
      } else {
        const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
        if (signUpError) throw signUpError;
        if (data.session) {
          onSuccess();
        } else {
          setInfo('Đã tạo tài khoản. Vui lòng kiểm tra email để xác nhận, sau đó đăng nhập.');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Đã xảy ra lỗi. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-10">
      <div className="text-center mb-6">
        <div className="w-12 h-12 mx-auto rounded-lg bg-primary-500/15 flex items-center justify-center mb-3">
          <i className="ri-customer-service-2-line text-primary-400 text-2xl"></i>
        </div>
        <h1 className="text-2xl font-bold text-foreground-950">Hộp thư hỗ trợ</h1>
        <p className="text-sm text-foreground-600 mt-1">Đăng nhập để xem và trả lời yêu cầu của khách hàng.</p>
      </div>

      <div className="rounded-xl border border-background-200/60 bg-background-100 p-6">
        <div className="flex rounded-full bg-background-50 border border-background-300/60 p-1 mb-5">
          <button
            onClick={() => setMode('signin')}
            className={`flex-1 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${mode === 'signin' ? 'bg-primary-500 text-background-950' : 'text-foreground-600'}`}
          >
            Đăng nhập
          </button>
          <button
            onClick={() => setMode('signup')}
            className={`flex-1 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${mode === 'signup' ? 'bg-primary-500 text-background-950' : 'text-foreground-600'}`}
          >
            Đăng ký
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-400 text-base"></i>
            <p className="text-sm text-rose-400">{error}</p>
          </div>
        )}
        {info && (
          <div className="mb-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-3 flex items-center gap-2">
            <i className="ri-information-line text-emerald-400 text-base"></i>
            <p className="text-sm text-emerald-400">{info}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="ban@example.com"
              className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Mật khẩu</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="••••••••"
              className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
          >
            {loading ? 'Vui lòng chờ...' : mode === 'signin' ? 'Đăng nhập' : 'Đăng ký'}
          </button>
        </form>
      </div>
    </div>
  );
}